import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Linkedin, Mail, Phone, Users, Star, Sparkles, Building2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import TeamMemberDialog from "./TeamMemberDialog";

const CountingNumber: React.FC<{ end: number; duration?: number; delay?: number }> = ({
    end,
    duration = 1000,
    delay = 0
}) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            let start = 0;
            const increment = end / (duration / 10);
            const counter = setInterval(() => {
                start += increment;
                if (start >= end) {
                    setCount(end);
                    clearInterval(counter);
                } else {
                    setCount(Math.floor(start));
                }
            }, 10);
            return () => clearInterval(counter);
        }, delay);

        return () => clearTimeout(timer);
    }, [end, duration, delay]);

    return <span>{count}</span>;
};

// Helper function to decode HTML entities and URL encoding
const decodeHtmlAndUrl = (str: string): string => {
    if (!str) return '';

    try {
        // First decode URL encoding
        let decoded = decodeURIComponent(str);

        // Then decode HTML entities - only on client side
        if (typeof document !== 'undefined') {
            const textArea = document.createElement('textarea');
            if (textArea) {
                textArea.innerHTML = decoded;
                decoded = textArea.value;
            }
        }

        return decoded;
    } catch (error) {
        console.warn('Error decoding HTML/URL:', error);
        return str; // Return original string if decoding fails
    }
};

export interface Staff {
    id?: string;
    firstname: string;
    lastname: string;
    fullname: string;
    email: string;
    office: string;
    mobile: string;
    mainphone: string;
    photourl: string;
    position: string;
    linkedinurl: string;
    regions: number[];
    bio: string;
}

export interface StaffList {
    status: string;
    statuscode: number;
    results: Staff[];
}

const GetStaff = () => {
    return axios.get<StaffList>(`https://www.harveynormancommercial.nz/api/v1/staff/`)
}

export default function TeamFinder() {
    const Staff = useQuery({
        queryKey: ["staff"],
        staleTime: 60 * 1000 * 300, // 5min cache time
        queryFn: () => GetStaff().then((res) => res.data),
    });

    const [searchQuery, setSearchQuery] = useState<string>("");
    const [selectedCity, setSelectedCity] = useState<string>("all");
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [sortBy, setSortBy] = useState<'name' | 'office' | 'position'>('name');
    const [showStats, setShowStats] = useState(false);

    // Animation trigger for stats
    useEffect(() => {
        const timer = setTimeout(() => setShowStats(true), 500);
        return () => clearTimeout(timer);
    }, []);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };

    const [selectedItem, setSelectedItem] = useState<Staff | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const handleDialogOpenChange = (open: boolean) => {
        // console.log("Dialog open state:", open);
        setIsDialogOpen(open);
    };



    const handleImageClick = (item: Staff) => {
        setSelectedItem(item);
        setIsDialogOpen(true);
    };

    // Helper function to shuffle array using Fisher-Yates algorithm
    const shuffleArray = <T,>(array: T[]): T[] => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    // Filter, sort, and randomize staff
    const filteredAndSortedStaff = Staff.data?.results
        ? (() => {
            let filtered = Staff.data.results.filter((item) => {
                if (!item) return false;
                const name = (item.fullname ?? "").toLowerCase();
                const city = (item.office ?? "").toLowerCase();
                const matchesSearch = name.includes((searchQuery ?? "").toLowerCase());
                const matchesCity =
                    selectedCity === "all" ||
                    selectedCity === "" ||
                    city === (selectedCity ?? "").toLowerCase();
                return matchesSearch && matchesCity;
            });


            filtered = filtered.sort((a, b) => {
                switch (sortBy) {
                    case 'name':
                        return (a.fullname || '').localeCompare(b.fullname || '');
                    case 'office':
                        return (a.office || '').localeCompare(b.office || '');
                    case 'position':
                        return (a.position || '').localeCompare(b.position || '');
                    default:
                        return 0;
                }
            });

            // Only shuffle if you want random order, not after sorting
            return filtered;
        })()
        : [];

    // Get team statistics with proper Set handling
    const teamStats = Staff.data?.results ? {
        total: Staff.data.results.length,
        offices: Array.from(new Set(Staff.data.results.map(member => member.office))).length,
        regions: Array.from(new Set(Staff.data.results.flatMap(member => member.regions))).length
    } : { total: 0, offices: 0, regions: 0 };


    return (
        <div className="bg-gradient-to-br from-gray-50 via-white to-gray-100 py-20">
            <div className="max-w-7xl mx-auto px-4">
                {/* Enhanced Header Section with Floating Elements */}
                <div className="text-center mb-16 relative">
                    {/* Floating decorative elements */}
                    <div className="absolute -top-10 -left-10 w-20 h-20 bg-harvey-red/10 rounded-full blur-xl animate-pulse"></div>
                    <div className="absolute -top-5 -right-15 w-32 h-32 bg-harvey-blue/10 rounded-full blur-2xl animate-pulse delay-1000"></div>

                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-harvey-red to-harvey-red/80 rounded-full mb-6 shadow-lg relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-harvey-red to-harvey-red/80 rounded-full animate-ping opacity-75"></div>
                        <Users className="w-10 h-10 text-white relative z-10" />
                    </div>

                    <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                        Meet Our <span className="text-gradient-red relative">
                            Expert Team
                            <Sparkles className="absolute -top-2 -right-8 w-6 h-6 text-harvey-red/60 animate-bounce" />
                        </span>
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-12">
                        Get to know the people who make great service and smart solutions simple.
                    </p>

                    {/* Animated Team Statistics */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                        {[
                            { label: "Team Members", value: teamStats.total, icon: Users, color: "harvey-red" },
                            { label: "Office Locations", value: teamStats.offices, icon: Building2, color: "harvey-blue" },
                            { label: "Years Experience", value: "50+", icon: Star, color: "harvey-orange" }
                        ].map((stat, index) => (
                            <div
                                key={stat.label}
                                className={`bg-white rounded-2xl p-6 shadow-lg border border-gray-100 transform transition-all duration-700 ${showStats ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
                            >
                                <div className={`w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3`}>
                                    <stat.icon className={`w-6 h-6 text-harvey-red`} />
                                </div>
                                <div className="text-2xl font-bold text-gray-900 mb-1">
                                    {typeof stat.value === 'number' && showStats ?
                                        <CountingNumber end={stat.value} duration={2000} delay={index * 150} /> :
                                        stat.value
                                    }
                                </div>
                                <div className="text-sm text-gray-600">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Enhanced Search and Filter Section */}
                <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 mb-12 relative overflow-hidden">
                    {/* Background pattern */}
                    <div className="absolute inset-0 opacity-5">
                        <div className="absolute inset-0 bg-dot-pattern"></div>
                    </div>

                    <div className="relative z-10">
                        <div className="flex flex-col lg:flex-row gap-6 mb-6">
                            <div className="flex-1">
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    Search Team Members
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                    <Input
                                        type="search"
                                        className="w-full pl-12 pr-4 py-4 text-gray-900 placeholder-gray-500 border border-gray-200 rounded-xl focus:ring-2 focus:ring-harvey-red/20 focus:border-harvey-red transition-all duration-200 bg-white focus:bg-white h-14"
                                        placeholder="Search by name..."
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                    />
                                </div>
                            </div>

                            <div className="flex-1">
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    Filter by Location
                                </label>
                                <Select
                                    value={selectedCity}
                                    onValueChange={setSelectedCity}
                                >
                                    <SelectTrigger className="w-full py-4 px-4 text-gray-900 border border-gray-200 rounded-xl focus:ring-2 focus:ring-harvey-red/20 focus:border-harvey-red transition-all duration-200 bg-white focus:bg-white h-14">
                                        <SelectValue placeholder="All Locations" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white text-gray-900">
                                        <SelectItem value="all" >All Locations</SelectItem>
                                        {Staff.isFetched &&
                                            Staff.data?.results
                                                .map((item) => item.office)
                                                .filter((value, index, self) => self.indexOf(value) === index)
                                                .sort((a, b) => a.localeCompare(b))
                                                .map((office, index) => (
                                                    <SelectItem key={index} value={office}>
                                                        {office}
                                                    </SelectItem>
                                                ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Additional Controls */}
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                            <div id="team-finder" className="flex items-center gap-4">
                                <label className="text-sm font-semibold text-gray-700">Sort by:</label>
                                <Select
                                    value={sortBy}
                                    onValueChange={(value) => setSortBy(value as 'name' | 'office' | 'position')}
                                >
                                    <SelectTrigger className="w-40 h-10 py-2 px-3 text-gray-900 border border-gray-200 rounded-lg focus:ring-2 focus:ring-harvey-red/20 focus:border-harvey-red transition-all duration-200 bg-white text-sm">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white text-gray-900">
                                        <SelectItem value="name">Name</SelectItem>
                                        <SelectItem value="office">Office</SelectItem>
                                        <SelectItem value="position">Position</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex items-center gap-4">
                                <span className="text-sm text-gray-600">
                                    Showing {filteredAndSortedStaff.length} of {teamStats.total} team members
                                </span>
                                <div className="flex bg-gray-100 rounded-lg p-1">
                                    <Button
                                        onClick={() => setViewMode('grid')}
                                        variant="ghost"
                                        size="sm"
                                        className={`px-3 py-1 rounded-md text-sm transition-all duration-200 ${viewMode === 'grid'
                                            ? 'bg-harvey-red text-white shadow-sm hover:bg-harvey-red/90'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`}
                                    >
                                        Grid
                                    </Button>
                                    <Button
                                        onClick={() => setViewMode('list')}
                                        variant="ghost"
                                        size="sm"
                                        className={`px-3 py-1 rounded-md text-sm transition-all duration-200 ${viewMode === 'list'
                                            ? 'bg-harvey-red text-white shadow-sm hover:bg-harvey-red/90'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`}
                                    >
                                        List
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            {/* Enhanced Team Grid Section */}
            <div className="max-w-7xl mx-auto px-4">
                {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                        {Staff.isFetched && Staff.data && (
                            filteredAndSortedStaff.length > 0 ? (
                                filteredAndSortedStaff.map((item, index) => (
                                    <div
                                        key={index}
                                        className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 overflow-hidden cursor-pointer relative"
                                        onClick={() => handleImageClick(item)}
                                    >
                                        <div className="p-6">
                                            {/* Enhanced Profile Image */}
                                            <div className="relative mb-6 flex justify-center">
                                                <div className="relative group/image">
                                                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-gray-200 to-gray-100 p-1">
                                                        <img
                                                            className="w-full h-full rounded-full object-cover border-2 border-white shadow-md group-hover:scale-105 transition-transform duration-300"
                                                            src={item.photourl || undefined}
                                                            alt={`${item.fullname} - ${item.position}`}
                                                            onError={(e) => {
                                                                const target = e.target as HTMLImageElement;
                                                                target.onerror = null;
                                                                target.style.display = "none";
                                                            }}
                                                        />
                                                    </div>
                                                    {/* Enhanced status indicator with pulse */}
                                                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                                                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                                    </div>
                                                    {/* Hover overlay on image */}
                                                    <div className="absolute inset-0 bg-harvey-red/10 rounded-full opacity-0 group-hover/image:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                                        <span className="text-harvey-red text-xs font-medium">View Profile</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Enhanced Member Info */}
                                            <div className="text-center mb-6">
                                                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-harvey-red transition-colors duration-200">
                                                    {item.fullname}
                                                </h3>
                                                <p className="text-sm font-medium text-harvey-red mb-1">
                                                    {item.position}
                                                </p>
                                                <p className="text-sm text-gray-500 flex items-center justify-center">
                                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    {item.office}
                                                </p>
                                            </div>

                                            {/* Enhanced Contact Actions */}
                                            <div className="flex justify-center space-x-3">
                                                {item.email && (
                                                    <a
                                                        href={`mailto:${item.email}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="btn group/btn inline-flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-harvey-red rounded-full transition-all duration-200 hover:scale-110"
                                                        title={`Email ${item.fullname}`}
                                                    >
                                                        <Mail className=" w-4 h-4 text-gray-600 group-hover/btn:text-white transition-colors duration-200" />
                                                    </a>
                                                )}
                                                {item.mobile && (
                                                    <a
                                                        href={`tel:${item.mobile}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="btn group/btn inline-flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-harvey-red rounded-full transition-all duration-200 hover:scale-110"
                                                        title={`Call ${item.fullname}`}
                                                    >
                                                        <Phone className="w-4 h-4 text-gray-600 group-hover/btn:text-white transition-colors duration-200" />
                                                    </a>
                                                )}
                                                {item.linkedinurl && (
                                                    <a
                                                        href={item.linkedinurl}
                                                        onClick={(e) => e.stopPropagation()}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn group/btn inline-flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-blue-600 rounded-full transition-all duration-200 hover:scale-110"
                                                        title={`View ${item.fullname}'s LinkedIn profile`}
                                                    >
                                                        <Linkedin className="w-4 h-4 text-gray-600 group-hover/btn:text-white transition-colors duration-200" />
                                                    </a>
                                                )}
                                            </div>
                                        </div>

                                        {/* Enhanced hover overlay with gradient */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-harvey-red/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl"></div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-span-full flex flex-col items-center justify-center py-16">
                                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6 relative">
                                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        <div className="absolute -top-2 -right-2">
                                            <Sparkles className="w-6 h-6 text-gray-300" />
                                        </div>
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No team members found</h3>
                                    <p className="text-gray-500 text-center max-w-md">
                                        We couldn't find any team members matching your search criteria. Try adjusting your filters or search terms.
                                    </p>
                                </div>
                            )
                        )}
                    </div>
                ) : (
                    /* List View */
                    <div className="space-y-4">
                        {Staff.isFetched && Staff.data && (
                            filteredAndSortedStaff.length > 0 ? (
                                filteredAndSortedStaff.map((item, index) => (
                                    <div
                                        key={index}
                                        className="group bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 overflow-hidden cursor-pointer"
                                        onClick={() => handleImageClick(item)}
                                    >
                                        <div className="p-6 flex items-center space-x-6">
                                            {/* List view profile image */}
                                            <div className="relative flex-shrink-0">
                                                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gray-200 to-gray-100 p-1">
                                                    <img
                                                        className="w-full h-full rounded-full object-cover border-2 border-white shadow-sm group-hover:scale-105 transition-transform duration-200"
                                                        src={item.photourl || undefined}
                                                        alt={`${item.fullname} - ${item.position}`}
                                                        onError={(e) => {
                                                            const target = e.target as HTMLImageElement;
                                                            target.onerror = null;
                                                            target.style.display = "none";
                                                        }}
                                                    />
                                                </div>
                                                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                                                    <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                                                </div>
                                            </div>

                                            {/* List view member info */}
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <h3 className="text-lg font-bold text-gray-900 group-hover:text-harvey-red transition-colors duration-200">
                                                            {item.fullname}
                                                        </h3>
                                                        <p className="text-sm font-medium text-harvey-red">
                                                            {item.position}
                                                        </p>
                                                        <p className="text-sm text-gray-500 flex items-center mt-1">
                                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            </svg>
                                                            {item.office}
                                                        </p>
                                                    </div>

                                                    {/* List view contact actions */}
                                                    <div className="flex items-center space-x-2">
                                                        {item.email && (
                                                            <a
                                                                href={`mailto:${item.email}`}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="btn group/btn inline-flex items-center justify-center w-8 h-8 bg-gray-100 hover:bg-harvey-red rounded-full transition-all duration-200"
                                                                title={`Email ${item.fullname}`}
                                                            >
                                                                <Mail className="w-3 h-3 text-gray-600 group-hover/btn:text-white transition-colors duration-200" />
                                                            </a>
                                                        )}
                                                        {item.mobile && (
                                                            <a
                                                                href={`tel:${item.mobile}`}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="btn group/btn inline-flex items-center justify-center w-8 h-8 bg-gray-100 hover:bg-harvey-red rounded-full transition-all duration-200"
                                                                title={`Call ${item.fullname}`}
                                                            >
                                                                <Phone className="w-3 h-3 text-gray-600 group-hover/btn:text-white transition-colors duration-200" />
                                                            </a>
                                                        )}
                                                        {item.linkedinurl && (
                                                            <a
                                                                href={item.linkedinurl}
                                                                onClick={(e) => e.stopPropagation()}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn group/btn inline-flex items-center justify-center w-8 h-8 bg-gray-100 hover:bg-blue-600 rounded-full transition-all duration-200"
                                                                title={`View ${item.fullname}'s LinkedIn profile`}
                                                            >
                                                                <Linkedin className="w-3 h-3 text-gray-600 group-hover/btn:text-white transition-colors duration-200" />
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="flex flex-col items-center justify-center py-16">
                                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No team members found</h3>
                                    <p className="text-gray-500 text-center max-w-md">
                                        We couldn't find any team members matching your search criteria. Try adjusting your filters or search terms.
                                    </p>
                                </div>
                            )
                        )}
                    </div>
                )}
            </div>

            {/* Redesigned Team Member Modal */}
            {isDialogOpen && selectedItem && (
                <TeamMemberDialog
                    open={isDialogOpen}
                    onOpenChange={handleDialogOpenChange}
                    selectedItem={selectedItem}
                    decodeHtmlAndUrl={decodeHtmlAndUrl}
                />
            )}

        </div>

    );
}


